import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Trophy, Sparkles, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/audit";

export const Route = createFileRoute("/r/$code")({
  loader: ({ params }) => ({ code: params.code }),
  head: ({ loaderData }) => {
    const title = "🌎 Te invitaron a VerseSport — Mundial 2026";
    const desc =
      "Únete al Mundial 2026 en VerseSport: predicciones en vivo, Nation Wars y recompensas reales.";
    const url = `https://versesport.com/r/${loaderData?.code ?? ""}`;
    const image = "https://versesport.com/og/mundial-2026.jpg";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: image },
        { name: "robots", content: "noindex,follow" },
      ],
    };
  },
  component: ReferralLandingPage,
});

function ReferralLandingPage() {
  const { code } = Route.useLoaderData();
  const navigate = useNavigate();

  useEffect(() => {
    void trackEvent("referral_landing_view", { code, ref_code: code });
    // Persist the ref so any later sign-up or guest action attributes back.
    try { window.localStorage.setItem("ss2026_incoming_ref", code); } catch { /* noop */ }
  }, [code]);

  const handleCta = (cta: "register" | "guest") => {
    void trackEvent("referral_link_clicked", { code, ref_code: code, cta });
    if (cta === "register") void navigate({ to: "/registro" });
    else void navigate({ to: "/", search: { ref: code } as never, replace: true });
  };

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-md space-y-6">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-fuchsia-500/15 via-background to-indigo-500/15 p-6 text-center shadow-xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-fuchsia-500 shadow-lg">
            <Trophy className="h-7 w-7 text-background" />
          </div>
          <p className="mt-3 text-[10px] font-black uppercase tracking-[0.25em] text-primary">
            Invitación Mundial 2026
          </p>
          <h1 className="mt-1 text-2xl font-black text-foreground">
            Te invitaron a VerseSport
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Únete al Mundial 2026: predicciones en vivo, Nation Wars y recompensas reales.
            Tu invitación queda vinculada a quien te trajo.
          </p>

          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => handleCta("register")}
              className="flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow"
            >
              <Sparkles className="h-4 w-4" /> Crear mi cuenta
            </button>
            <button
              type="button"
              onClick={() => handleCta("guest")}
              className="flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-sm font-bold text-foreground"
            >
              Entrar como invitado <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">
            Código de referido: <span className="font-mono font-black text-foreground">{code}</span>
          </p>
        </div>

        <p className="text-center text-[11px] text-muted-foreground">
          Después elegirás tu nación al entrar a Nation Wars, Quinielas o Creator Studio.{" "}
          <Link to="/showcase" className="font-bold text-primary underline">
            Explora el Showcase
          </Link>
        </p>
      </div>
    </main>
  );
}